import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { LoginConstants } from '../config/keyClock.config';
import { Variants } from '@/types/common';
import { showAlert } from '../slice/AlertSlice';
import { hasStoredContext } from '../services/current-context';
import { useLazyGetExternalUserQuery } from '../components/orders/services/orderApi';
import { resolveEntityScope } from '../services/entity-scope-resolution';
import { readPersistedScope } from '../services/entity-scope-persistence';
import { scopeHydrated } from '../slice/entityScopeSlice';
import {
  consumeIntendedUrl,
  peekIntendedUrl,
} from '../services/intended-navigation';
import { useNavigationService } from '../services/navigate-service';

/**
 * The post-login entity-scope bootstrap, shared by the app's two login
 * surfaces: `Loader` at '/' for a returning session, and `CallBack` after the
 * token exchange (DV-3012 §3.1 / D1). `bootstrapAuth` cannot cover the second
 * — '/callback' loads before a token exists — so the resolution step needs
 * this entry point as well as that one.
 *
 * Both screens used to hold their own copy of "fetch the role-appropriate org
 * list, then route". That resolution now lives in `resolveEntityScope`; this
 * hook holds the other half of spec §3.4's split — **the step owns
 * navigation, the caller owns rendering** — so that half is not duplicated
 * either. This is DV-3188 ("extract the duplicated post-login org bootstrap
 * into one shared hook"), which Phase B absorbs.
 *
 * The registration check runs here for the same reason: it is the step that
 * precedes scope resolution on BOTH surfaces, and keeping it in `CallBack`
 * alone was a defect — a user who abandoned the additional-details form and
 * came back to '/' resolved with no orgs and dead-ended on
 * `<NoOrganizations />`, whose only action is Sign Out, instead of being
 * returned to the form.
 *
 * @returns `noOrganizations` — render `<NoOrganizations />` instead of the
 *   spinner — and `resolveScopeAndLand`, to be called once the caller has a
 *   usable token.
 */
export const useScopeBootstrap = (): {
  noOrganizations: boolean;
  resolveScopeAndLand: () => Promise<void>;
} => {
  const { navigateTo } = useNavigationService();
  const dispatch = useDispatch();
  const [triggerGetExternalUser] = useLazyGetExternalUserQuery();
  const [noOrganizations, setNoOrganizations] = useState(false);

  /**
   * Send a user who has not finished registering back to the additional-
   * details form, and report whether that happened.
   *
   * Both terms of the original condition are kept, in the cheap-first order:
   * a user with a context has a scope to resolve, so the user record cannot
   * change the answer and is not fetched. The context is read through
   * `hasStoredContext()`, which asks whether one was STORED. This used to ask
   * `getCurrentContext()` — whether it parses to a display NAME — so an
   * entity whose name is exactly a role suffix read as "never registered".
   * Both still share the ABSENT set, which is the migration for the legacy
   * literal "undefined" (§6.4 / D12).
   */
  const routeIncompleteRegistration = useCallback(async (): Promise<boolean> => {
    if (hasStoredContext()) return false;
    const userId = localStorage.getItem('userId');
    // No userId means the token exchange has not written one yet; there is
    // nothing to look the user record up by. Scope resolution's own wiped-
    // state check (entity-scope-resolution.ts) owns that case.
    if (!userId) return false;

    const entityData = await triggerGetExternalUser(userId).unwrap();
    if (entityData?.entity != null) return false;

    localStorage.setItem('entityData', JSON.stringify(entityData));
    navigateTo(LoginConstants.NAVIGATE_TO_ADDITIONAL_DETAILS, {
      userId: entityData.userId,
    });
    return true;
  }, [navigateTo, triggerGetExternalUser]);

  const resolveScopeAndLand = useCallback(async () => {
    // '/callback' loads before a token exists, so bootstrapAuth bailed before
    // hydrating. Both login surfaces reach the step through this hook, so this
    // is the one place that covers the fresh-login path (spec §3.1 / D2).
    //
    // Dispatched through the hook's own `dispatch` rather than through
    // `getStore()`: this runs inside the Provider, so the store it should
    // write to is the mounted one. The registry exists for the pre-render
    // regime (`bootstrapAuth`), which has no Provider to read from.
    dispatch(scopeHydrated(readPersistedScope()));

    try {
      if (await routeIncompleteRegistration()) return;
    } catch (error: unknown) {
      // The user-record lookup failed. Surfacing it is what `CallBack` did
      // when it owned this call; resolving scope on top of an unknown
      // registration state would only produce a second, less legible error.
      dispatch(showAlert({ type: Variants.ERROR, apiResponse: error }));
      return;
    }

    // Resolve the scope of where the user is GOING, not of the login surface
    // they are standing on. '/' and '/callback' are both `entity: 'any'`, so
    // resolving either would fall straight through §3.3 to `{ ready }` and
    // skip step 4 — the picker route, the single-org auto-select and the
    // DV-3175 dead end — which is exactly the work these two screens used to
    // do for themselves. A stored deep link resolves against its own route's
    // requirement; with none, the default landing is the destination.
    const destination =
      peekIntendedUrl()?.pathname ?? LoginConstants.NAVIGATE_TO;
    const outcome = await resolveEntityScope(destination);

    if (outcome.kind === 'ready' || outcome.kind === 'switching') {
      // The user is correctly scoped, so the step had no navigation to own —
      // and neither login surface renders anything they can stay on. Landing
      // them is route *landing*, the whole purpose of '/', and it is what
      // keeps today's behaviour.
      //
      // Note the asymmetry: the step only PEEKS the intended URL, because
      // only `switchEntity`'s 'land-intended' mode consumes it (D7/BC-2).
      // No switch happened on this branch, so the consume is ours.
      //
      // 'switching' is defensive rather than reachable from here: it requires
      // an `entityUUID` on the CURRENT url, and neither surface carries one
      // ('/callback' carries `code`/`state`). It is handled alongside 'ready'
      // because the user is correctly scoped either way.
      const intended = consumeIntendedUrl();
      if (intended) {
        navigateTo(intended.pathname, intended.queryParams);
      } else {
        navigateTo(LoginConstants.NAVIGATE_TO);
      }
      return;
    }

    if (outcome.kind === 'no-organizations') {
      // DV-3175: rendered, never navigated. Routing to '/' would re-enter
      // this same bootstrap, which is how the original defect looped.
      setNoOrganizations(true);
      return;
    }

    // 'navigating' and 'denied': the step has already started a trip and this
    // surface has nothing to render but the spinner. The trip is a full-page
    // one for the picker route and '/error?reason=…', and — since DV-3013 PR 3
    // wired the navigation port inside AuthenticatedShell — a ROUTER landing
    // for a switch that reaches here. Either way this component unmounts when
    // it completes, so holding the spinner remains the correct handling.
  }, [navigateTo, dispatch, routeIncompleteRegistration]);

  return { noOrganizations, resolveScopeAndLand };
};
